<template>
    <wrapper-features-templates-one-column
    hideClose
    showFooter
    fullWhiteBox
    nonAuthenticatedFeature
    :customBreadcrumbs="{ currentArea: t('initialActions.chooseDefaultContractAccountGroup.breadcrumb') }"
    :footerButtonsParams="footerButtonsParams">
        <!-- Insert content -->
        <table-generic
        hideHeader
        :hasDetail="false"
        :headerData="tableHeaderData"
        :tableData="tableData"
        :tableStyles="tableStyles"
        @selectedRadio="radioClickHandler($event)"/>
    </wrapper-features-templates-one-column>
    <!-- Confirmation Modal -->
    <modal-centered-generic
    v-if="showConfimationModal"
    titleIcon="document-confirm"
    rightButtonIcon="check-line"
    :title="t('initialActions.chooseDefaultContractAccountGroup.modalTitle')"
    :text="t('initialActions.chooseDefaultContractAccountGroup.modalText', {accountName: selectedRow.find(el => el.dataAttr === 'name').value})"
    :leftButtonText="t('initialActions.chooseDefaultContractAccountGroup.leftBtn')"
    :rightButtonText="t('initialActions.chooseDefaultContractAccountGroup.rightBtn')"
    @hideMe="showConfimationModal = false"
    @leftButtonClicked="saveDefaultHandler()"
    @rightButtonClicked="saveDefaultHandler('defineDefaultAccountsGroup')">
        <selector-checkbox
        style="margin-top: 16px; align-self: flex-start;"
        :label="t('initialActions.chooseDefaultContractAccountGroup.dontShowAgain')"
        :selected="dontShowThisAgain"
        @clicked="dontShowThisAgain = !dontShowThisAgain"/>
    </modal-centered-generic>
    <!-- Loader -->
    <loader-generic v-if="showLoader" isOverlay/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import TableGeneric from '@/components/generic/table/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import SelectorCheckbox from '@/components/generic/selectors/checkbox/Checkbox'
import LoaderGeneric from '@/components/generic/loaders/Generic'
/* Assets & Helpers */
import { parseToSelectRowRadio } from '@/assets/scripts/parsers/tables'

/* vue */
import { ref, computed, onMounted } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Helpers */
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { forceLogout, updateDefaultAccountsGroup, changeAccountGroupOfSession, updateAccountGroupInitialAction } = mapActions(moduleMapper.SESSION)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { updateMenu } = mapActions(moduleMapper.MENU)
const { updateAppThemeClientType } = mapActions()
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['updateLoginLoader'])

emit('updateLoginLoader', false)
const showConfimationModal = ref(false)
const dontShowThisAgain = ref(false)
const showLoader = ref(false)

const hasConfirmationModalInitialAction = computed(() => {
    return getAllCustomerInfo.value.initialActions.length && getAllCustomerInfo.value.initialActions.find(obj => obj.type === 'CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP')
})

const footerButtonsParams = ref([{
    label: t('initialActions.chooseDefaultContractAccountGroup.backToLogin'),
    callbackFunction: () => forceLogout()
}, {
    label: t('initialActions.chooseDefaultContractAccountGroup.goIn'),
    icon: 'arrow-right-full',
    buttonType: 'primary',
    iconPosition: 'right',
    callbackFunction: hasConfirmationModalInitialAction.value ? () => { showConfimationModal.value = true } : () => saveDefaultHandler()
}])

const tableHeaderData = [
	{ rowCellType: 'radio', showInMobile: true },
	{ rowCellType: 'acronym', showInMobile: true },
	{ rowCellType: 'title', showInMobile: { groupWith: 'description' } },
	{ rowCellType: 'description' }
]

const tableStyles = [null, { padding: '0' }, null, { 'text-align': 'end' }]
const tableData = ref(
    getAllCustomerInfo.value.accountGroups.filter(ele => ele.type !== 'PARTICULAR').map(ele => {
        return [
            { value: ele.id, dataAttr: 'id' },
            { type: 'radio', value: ele.default, dataAttr: 'type' },
            { type: 'acronym', value: ele.name, dataAttr: 'acronym' },
            { type: 'title', value: ele.name, dataAttr: 'name' },
            { type: 'description', value: t('initialActions.chooseDefaultContractAccountGroup.associatedAccounts', { accounts: ele.accountQuantity }), dataAttr: 'description' }
        ]
    })
)

const radioClickHandler = $event => {
	tableData.value = parseToSelectRowRadio(tableData.value, $event.rowId)
}

const selectedRow = computed(() => {
    return tableData.value.find(el => el.find(ele => ele.type === 'radio').value)
})

const proceedToEnterApp = () => {
    !getMobileAppState.value ? navigateByPath(routeList.HOME_PATH) : navigateByPath(routeList.HOME_MOBILE_PATH)
}

const saveDefaultHandler = async (defineDefaultAccountsGroup) => {
    showLoader.value = true
    const selectedAccountGroup = getAllCustomerInfo.value.accountGroups.find(obj => obj.id === selectedRow.value[0].value)

    try {
        const payload = { sequenceNumber: selectedAccountGroup.id }

        if (defineDefaultAccountsGroup) await updateDefaultAccountsGroup(payload)
        if (dontShowThisAgain.value) await updateAccountGroupInitialAction()

        const res = await changeAccountGroupOfSession({
            data: { groupId: payload.sequenceNumber, returnMenu: true },
            accountGroupType: selectedAccountGroup.type
        })
        updateAppThemeClientType(selectedAccountGroup.type.toLowerCase())
        updateMenu(res.data.data.menuItemList)
        proceedToEnterApp()
    } catch (error) {
		errorHandler(error.response?.data)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor([{ ...data, dismissToastBtnText: t('login.errorFeedbackToast') }], retryFn)
	showFeedbackToast.value = true
}

onMounted(() => {
    if (!selectedRow.value) tableData.value = parseToSelectRowRadio(tableData.value, tableData.value[0][0].value)
})
</script>

<style lang="scss" scoped>
.table-bed {
    width: 508px;
    max-width: 100%;
    align-self: center;
}
</style>
